export interface HospitalSetting {
  cacheReady?: boolean
  id: number
  hospitalSettingUuid: string
  hospitalSettingName: string
  hospitalSettingData: Record<string, any>
  createdDt: string
  updatedDt: string
}

export interface UninsuredDiscountItem {
  discountId: string
  discountName: string
  discountRate: number
}

export interface UninsuredDiscountSettingsData {
  discounts: UninsuredDiscountItem[]
}

export const DEFAULT_UNINSURED_DISCOUNT_SETTINGS: UninsuredDiscountSettingsData = {
  discounts: [
    { discountId: "family", discountName: "가족할인", discountRate: 30 },
    { discountId: "friend", discountName: "지인할인", discountRate: 20 },
    { discountId: "staff", discountName: "직원할인", discountRate: 20 },
    { discountId: "loyalty", discountName: "연복할인", discountRate: 10 },
    { discountId: "etc", discountName: "기타할인", discountRate: 5 },
  ],
}

export const createDefaultUninsuredDiscountSettings = (): UninsuredDiscountSettingsData => ({
  discounts: DEFAULT_UNINSURED_DISCOUNT_SETTINGS.discounts.map(discount => ({ ...discount })),
})

export const normalizeUninsuredDiscountSettingsData = (value: unknown): UninsuredDiscountSettingsData => {
  if (!value || typeof value !== "object") {
    return createDefaultUninsuredDiscountSettings()
  }

  const rawDiscounts = Array.isArray((value as { discounts?: unknown[] }).discounts) ? (value as { discounts: unknown[] }).discounts : []

  const discounts = rawDiscounts
    .map((discount, index) => {
      if (!discount || typeof discount !== "object") {
        return null
      }

      const rawDiscount = discount as Partial<UninsuredDiscountItem>
      const discountName = typeof rawDiscount.discountName === "string" ? rawDiscount.discountName.trim() : ""
      const parsedRate = Number(rawDiscount.discountRate)
      const discountRate = Number.isFinite(parsedRate) ? Math.min(100, Math.max(0, Math.round(parsedRate))) : 0
      const discountId =
        typeof rawDiscount.discountId === "string" && rawDiscount.discountId.trim() ? rawDiscount.discountId.trim() : `discount-${index + 1}`

      if (!discountName) {
        return null
      }

      return {
        discountId,
        discountName,
        discountRate,
      }
    })
    .filter((discount): discount is UninsuredDiscountItem => discount !== null)

  if (discounts.length === 0) {
    return createDefaultUninsuredDiscountSettings()
  }

  return { discounts }
}

export interface SecuritySettingData {
  passwordChangeInterval: string
}

export interface KakaoAlimtalkSettingsData {
  enabled: boolean
  favoriteTemplateUuids: string[]
  paymentAutoSend: PaymentAutoAlimtalkSettingsData
}

export interface PaymentAutoAlimtalkRuleData {
  enabled: boolean
  firstVisit: boolean
  returnVisit: boolean
  templateUuid: string
}

export interface PaymentCustomAutoAlimtalkRuleData extends PaymentAutoAlimtalkRuleData {
  ruleUuid: string
  uninsuredName: string
}

export interface PaymentAutoAlimtalkSettingsData {
  enabled: boolean
  chuna: PaymentAutoAlimtalkRuleData
  package: PaymentAutoAlimtalkRuleData
  custom: PaymentCustomAutoAlimtalkRuleData[]
}

// 통합 보안 설정 데이터 타입
export interface SecuritySettingsData {
  passwordChangeInterval: string
  autoLogoutDurationMinutes: number
}

export const DELAYED_REFRESH_DEBOUNCE_SECONDS_OPTIONS = [1, 2, 3, 4, 5] as const
export const DELAYED_REFRESH_MAX_WAIT_SECONDS_OPTIONS = [2, 3, 5, 10] as const

export interface BetaSettingsData {
  betaEnabled: boolean
  crmEnabled: boolean
  pharmaOneButtonEnabled: boolean
  aiAnalysisEnabled: boolean
  gptEnabled: boolean
  commandBotEnabled: boolean
  cardTerminalEnabled: boolean
  doctalkEnabled: boolean
  msureSilsonEnabled: boolean
  nhisChupyakEnabled: boolean
  paymentBalanceFeatureEnabled: boolean
  serverQualificationEnabled: boolean
  simpleQualificationEnabled: boolean
  simpleHiraSubmissionEnabled: boolean
  serverHiraEnabled: boolean
  medicalRecordExternalNoteEnabled: boolean
  digitalSignatureEnabled: boolean
  memberAvatarEnabled: boolean
  penChartEnabled: boolean
  patientAnalysisEnabled: boolean
  packageUsageEnabled: boolean
  prepaymentUsageEnabled: boolean
  diagnosisPatternAssistantEnabled: boolean
  kakaoMessageEnabled: boolean
  medicalNoteAutoSaveEnabled: boolean
  collaborativeMedicalRecordEnabled: boolean
  encounterInsuranceChangeEnabled: boolean
  dockEnabled: boolean
  fastFeeLookupEnabled: boolean
  primaryInsuranceEnabled: boolean
  patientListIndividualRefreshEnabled: boolean
  delayedRefreshEnabled: boolean
  delayedRefreshDebounceSeconds: (typeof DELAYED_REFRESH_DEBOUNCE_SECONDS_OPTIONS)[number]
  delayedRefreshMaxWaitSeconds: (typeof DELAYED_REFRESH_MAX_WAIT_SECONDS_OPTIONS)[number]
}

export const createDefaultBetaSettingsData = (): BetaSettingsData => ({
  betaEnabled: false,
  crmEnabled: false,
  pharmaOneButtonEnabled: false,
  aiAnalysisEnabled: false,
  gptEnabled: false,
  commandBotEnabled: false,
  cardTerminalEnabled: false,
  doctalkEnabled: false,
  msureSilsonEnabled: false,
  nhisChupyakEnabled: false,
  paymentBalanceFeatureEnabled: true,
  serverQualificationEnabled: false,
  simpleQualificationEnabled: false,
  simpleHiraSubmissionEnabled: false,
  serverHiraEnabled: false,
  medicalRecordExternalNoteEnabled: true,
  digitalSignatureEnabled: false,
  memberAvatarEnabled: true,
  penChartEnabled: false,
  patientAnalysisEnabled: true,
  packageUsageEnabled: true,
  prepaymentUsageEnabled: false,
  diagnosisPatternAssistantEnabled: false,
  kakaoMessageEnabled: false,
  medicalNoteAutoSaveEnabled: false,
  collaborativeMedicalRecordEnabled: false,
  encounterInsuranceChangeEnabled: false,
  dockEnabled: false,
  fastFeeLookupEnabled: false,
  primaryInsuranceEnabled: false,
  patientListIndividualRefreshEnabled: false,
  delayedRefreshEnabled: false,
  delayedRefreshDebounceSeconds: 1,
  delayedRefreshMaxWaitSeconds: 2,
})

const normalizeBetaSettingSeconds = <Value extends number>(value: unknown, options: readonly Value[], defaultValue: Value): Value => {
  if (typeof value !== "number" && typeof value !== "string") {
    return defaultValue
  }
  const seconds = Number(value) as Value
  return options.includes(seconds) ? seconds : defaultValue
}

const normalizeBetaSettingBoolean = (value: unknown, defaultValue = false): boolean => {
  if (typeof value === "boolean") {
    return value
  }

  if (typeof value === "number") {
    return value !== 0
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true
    }
    if (["false", "0", "no", "off", ""].includes(normalized)) {
      return false
    }
  }

  return defaultValue
}

export const normalizeBetaSettingsData = (value: unknown): BetaSettingsData => {
  if (!value || typeof value !== "object") {
    return createDefaultBetaSettingsData()
  }

  const rawValue = value as Record<string, unknown>
  const betaEnabled = rawValue.betaEnabled ?? rawValue.beta_enabled
  const aiAnalysisEnabled = rawValue.aiAnalysisEnabled ?? rawValue.ai_analysis_enabled
  const gptEnabled = rawValue.gptEnabled ?? rawValue.gpt_enabled
  const commandBotEnabled = rawValue.commandBotEnabled ?? rawValue.command_bot_enabled
  const cardTerminalEnabled = rawValue.cardTerminalEnabled ?? rawValue.card_terminal_enabled
  const doctalkEnabled = rawValue.doctalkEnabled ?? rawValue.doctalk_enabled
  const msureSilsonEnabled = rawValue.msureSilsonEnabled ?? rawValue.msure_silson_enabled
  const nhisChupyakEnabled = rawValue.nhisChupyakEnabled ?? rawValue.nhis_chupyak_enabled
  const paymentBalanceFeatureEnabled = rawValue.paymentBalanceFeatureEnabled ?? rawValue.payment_balance_feature_enabled
  const serverQualificationEnabled = rawValue.serverQualificationEnabled ?? rawValue.server_qualification_enabled
  const serverHiraEnabled = rawValue.serverHiraEnabled ?? rawValue.server_hira_enabled
  const medicalRecordExternalNoteEnabled = rawValue.medicalRecordExternalNoteEnabled ?? rawValue.medical_record_external_note_enabled
  const digitalSignatureEnabled =
    rawValue.digitalSignatureEnabled ??
    rawValue.digital_signature_enabled ??
    rawValue.electronicSignatureEnabled ??
    rawValue.electronic_signature_enabled
  const memberAvatarEnabled = rawValue.memberAvatarEnabled ?? rawValue.member_avatar_enabled
  const penChartEnabled = rawValue.penChartEnabled ?? rawValue.penchartEnabled ?? rawValue.pen_chart_enabled ?? rawValue.penchart_enabled
  const patientAnalysisEnabled = rawValue.patientAnalysisEnabled ?? rawValue.patient_analysis_enabled
  const packageUsageEnabled = rawValue.packageUsageEnabled ?? rawValue.package_usage_enabled
  const prepaymentUsageEnabled = rawValue.prepaymentUsageEnabled ?? rawValue.prepayment_usage_enabled
  const diagnosisPatternAssistantEnabled = rawValue.diagnosisPatternAssistantEnabled ?? rawValue.diagnosis_pattern_assistant_enabled
  const kakaoMessageEnabled = rawValue.kakaoMessageEnabled ?? rawValue.kakao_message_enabled
  const medicalNoteAutoSaveEnabled = rawValue.medicalNoteAutoSaveEnabled ?? rawValue.medical_note_auto_save_enabled
  const dockEnabled = rawValue.dockEnabled ?? rawValue.dock_enabled
  const fastFeeLookupEnabled = rawValue.fastFeeLookupEnabled ?? rawValue.fast_fee_lookup_enabled
  const patientListIndividualRefreshEnabled = rawValue.patientListIndividualRefreshEnabled ?? rawValue.patient_list_individual_refresh_enabled
  const delayedRefreshEnabled = rawValue.delayedRefreshEnabled ?? rawValue.delayed_refresh_enabled
  const delayedRefreshDebounceSeconds = rawValue.delayedRefreshDebounceSeconds ?? rawValue.delayed_refresh_debounce_seconds
  const delayedRefreshMaxWaitSeconds = rawValue.delayedRefreshMaxWaitSeconds ?? rawValue.delayed_refresh_max_wait_seconds

  return {
    betaEnabled: normalizeBetaSettingBoolean(betaEnabled),
    crmEnabled: normalizeBetaSettingBoolean(rawValue.crmEnabled !== undefined ? rawValue.crmEnabled : rawValue.crm_enabled),
    pharmaOneButtonEnabled: normalizeBetaSettingBoolean(rawValue.pharmaOneButtonEnabled ?? rawValue.pharma_one_button_enabled),
    aiAnalysisEnabled: normalizeBetaSettingBoolean(aiAnalysisEnabled),
    gptEnabled: normalizeBetaSettingBoolean(gptEnabled),
    commandBotEnabled: normalizeBetaSettingBoolean(commandBotEnabled),
    cardTerminalEnabled: normalizeBetaSettingBoolean(cardTerminalEnabled),
    doctalkEnabled: normalizeBetaSettingBoolean(doctalkEnabled),
    msureSilsonEnabled: normalizeBetaSettingBoolean(msureSilsonEnabled),
    nhisChupyakEnabled: normalizeBetaSettingBoolean(nhisChupyakEnabled),
    paymentBalanceFeatureEnabled: true,
    serverQualificationEnabled: normalizeBetaSettingBoolean(serverQualificationEnabled),
    simpleQualificationEnabled: normalizeBetaSettingBoolean(rawValue.simpleQualificationEnabled ?? rawValue.simple_qualification_enabled),
    simpleHiraSubmissionEnabled: normalizeBetaSettingBoolean(rawValue.simpleHiraSubmissionEnabled ?? rawValue.simple_hira_submission_enabled),
    serverHiraEnabled: normalizeBetaSettingBoolean(serverHiraEnabled),
    medicalRecordExternalNoteEnabled: normalizeBetaSettingBoolean(medicalRecordExternalNoteEnabled, true),
    digitalSignatureEnabled: normalizeBetaSettingBoolean(digitalSignatureEnabled),
    memberAvatarEnabled: true,
    penChartEnabled: normalizeBetaSettingBoolean(penChartEnabled),
    patientAnalysisEnabled: true,
    packageUsageEnabled: true,
    prepaymentUsageEnabled: normalizeBetaSettingBoolean(prepaymentUsageEnabled),
    diagnosisPatternAssistantEnabled: normalizeBetaSettingBoolean(diagnosisPatternAssistantEnabled),
    kakaoMessageEnabled: normalizeBetaSettingBoolean(kakaoMessageEnabled),
    medicalNoteAutoSaveEnabled: normalizeBetaSettingBoolean(medicalNoteAutoSaveEnabled),
    collaborativeMedicalRecordEnabled: normalizeBetaSettingBoolean(
      rawValue.collaborativeMedicalRecordEnabled ?? rawValue.collaborative_medical_record_enabled
    ),
    encounterInsuranceChangeEnabled: normalizeBetaSettingBoolean(
      rawValue.encounterInsuranceChangeEnabled ?? rawValue.encounter_insurance_change_enabled
    ),
    dockEnabled: normalizeBetaSettingBoolean(dockEnabled),
    fastFeeLookupEnabled: normalizeBetaSettingBoolean(fastFeeLookupEnabled),
    primaryInsuranceEnabled: normalizeBetaSettingBoolean(rawValue.primaryInsuranceEnabled ?? rawValue.primary_insurance_enabled),
    patientListIndividualRefreshEnabled: normalizeBetaSettingBoolean(patientListIndividualRefreshEnabled),
    delayedRefreshEnabled: normalizeBetaSettingBoolean(delayedRefreshEnabled),
    delayedRefreshDebounceSeconds: normalizeBetaSettingSeconds(delayedRefreshDebounceSeconds, DELAYED_REFRESH_DEBOUNCE_SECONDS_OPTIONS, 1),
    delayedRefreshMaxWaitSeconds: normalizeBetaSettingSeconds(delayedRefreshMaxWaitSeconds, DELAYED_REFRESH_MAX_WAIT_SECONDS_OPTIONS, 2),
  }
}

export type DoctalkReservationEnvironment = "dev" | "prod"

export const NAVER_RESERVATION_MEMO_FIELD_KEYS = [
  "reservationName",
  "userRequest",
  "itemOptions",
  "customBirth",
  "customGender",
  "customOther",
] as const

export type NaverReservationMemoFieldKey = (typeof NAVER_RESERVATION_MEMO_FIELD_KEYS)[number]

const DEFAULT_DOCTALK_PROD_ACCESS_KEY = "3BhoD9H0-2oQP-4gKU-aSrr-17QXWFjaTvmi"

export interface DoctalkReservationSettingsData {
  enabled: boolean
  internalOutboundEnabled: boolean
  environment: DoctalkReservationEnvironment
  useDummyPollingResponse: boolean
  skipReadPollingApi: boolean
  emrAccountId: string
  doctorUuid: string
  accessKey: string
  devAccessKey: string
  prodAccessKey: string
  hospitalIdOverride: string
  hospitalId: string
  actualHospitalId: string
  reservationKeys: string[]
  lastFetchedProducts: Record<string, unknown>[]
  pollingIntervalMinutes: number
  lastPolledAt: string | null
  lastSuccessAt: string | null
  lastErrorMessage: string
  naverMemoFieldOrder: NaverReservationMemoFieldKey[]
}

export const createDefaultDoctalkReservationSettingsData = (): DoctalkReservationSettingsData => ({
  enabled: false,
  internalOutboundEnabled: true,
  environment: "prod",
  useDummyPollingResponse: false,
  skipReadPollingApi: false,
  emrAccountId: "",
  doctorUuid: "",
  accessKey: DEFAULT_DOCTALK_PROD_ACCESS_KEY,
  devAccessKey: "",
  prodAccessKey: DEFAULT_DOCTALK_PROD_ACCESS_KEY,
  hospitalIdOverride: "",
  hospitalId: "",
  actualHospitalId: "",
  reservationKeys: [],
  lastFetchedProducts: [],
  pollingIntervalMinutes: 10,
  lastPolledAt: null,
  lastSuccessAt: null,
  lastErrorMessage: "",
  naverMemoFieldOrder: [...NAVER_RESERVATION_MEMO_FIELD_KEYS],
})

const normalizeDoctalkReservationText = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : ""
}

const normalizeNullableDoctalkReservationText = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null
  }

  const normalized = value.trim()
  return normalized || null
}

export const normalizeDoctalkReservationSettingsData = (value: unknown): DoctalkReservationSettingsData => {
  const defaults = createDefaultDoctalkReservationSettingsData()

  if (!value || typeof value !== "object") {
    return defaults
  }

  const rawValue = value as Record<string, unknown>
  const environmentValue = rawValue.environment
  const pollingIntervalMinutesValue = rawValue.pollingIntervalMinutes ?? rawValue.polling_interval_minutes
  const reservationKeysValue = rawValue.reservationKeys ?? rawValue.reservation_keys
  const lastFetchedProductsValue = rawValue.lastFetchedProducts ?? rawValue.last_fetched_products
  const naverMemoFieldOrderValue = rawValue.naverMemoFieldOrder ?? rawValue.naver_memo_field_order
  const parsedPollingIntervalMinutes = Number(pollingIntervalMinutesValue)

  const reservationKeys = Array.isArray(reservationKeysValue)
    ? reservationKeysValue
        .filter((reservationKey): reservationKey is string => typeof reservationKey === "string")
        .map(reservationKey => reservationKey.trim())
        .filter((reservationKey, index, list) => reservationKey.length > 0 && list.indexOf(reservationKey) === index)
    : defaults.reservationKeys

  const lastFetchedProducts = Array.isArray(lastFetchedProductsValue)
    ? lastFetchedProductsValue.filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    : defaults.lastFetchedProducts

  const naverMemoFieldOrder = Array.isArray(naverMemoFieldOrderValue)
    ? naverMemoFieldOrderValue.filter(
        (fieldKey, index, list): fieldKey is NaverReservationMemoFieldKey =>
          typeof fieldKey === "string" &&
          NAVER_RESERVATION_MEMO_FIELD_KEYS.includes(fieldKey as NaverReservationMemoFieldKey) &&
          list.indexOf(fieldKey) === index
      )
    : defaults.naverMemoFieldOrder

  return {
    enabled: normalizeBetaSettingBoolean(rawValue.enabled, defaults.enabled),
    internalOutboundEnabled: normalizeBetaSettingBoolean(
      rawValue.internalOutboundEnabled ?? rawValue.internal_outbound_enabled,
      defaults.internalOutboundEnabled
    ),
    environment: environmentValue === "dev" || environmentValue === "prod" ? environmentValue : defaults.environment,
    useDummyPollingResponse: normalizeBetaSettingBoolean(
      rawValue.useDummyPollingResponse ?? rawValue.use_dummy_polling_response,
      defaults.useDummyPollingResponse
    ),
    skipReadPollingApi: normalizeBetaSettingBoolean(rawValue.skipReadPollingApi ?? rawValue.skip_read_polling_api, defaults.skipReadPollingApi),
    emrAccountId: normalizeDoctalkReservationText(rawValue.emrAccountId ?? rawValue.emr_account_id),
    doctorUuid: normalizeDoctalkReservationText(rawValue.doctorUuid ?? rawValue.doctor_uuid),
    accessKey: normalizeDoctalkReservationText(rawValue.accessKey ?? rawValue.access_key),
    devAccessKey: normalizeDoctalkReservationText(rawValue.devAccessKey ?? rawValue.dev_access_key),
    prodAccessKey: normalizeDoctalkReservationText(rawValue.prodAccessKey ?? rawValue.prod_access_key),
    hospitalIdOverride: normalizeDoctalkReservationText(rawValue.hospitalIdOverride ?? rawValue.hospital_id_override),
    hospitalId: normalizeDoctalkReservationText(rawValue.hospitalId ?? rawValue.hospital_id),
    actualHospitalId: normalizeDoctalkReservationText(rawValue.actualHospitalId ?? rawValue.actual_hospital_id),
    reservationKeys,
    lastFetchedProducts,
    pollingIntervalMinutes:
      Number.isInteger(parsedPollingIntervalMinutes) && parsedPollingIntervalMinutes >= 1
        ? parsedPollingIntervalMinutes
        : defaults.pollingIntervalMinutes,
    lastPolledAt: normalizeNullableDoctalkReservationText(rawValue.lastPolledAt ?? rawValue.last_polled_at),
    lastSuccessAt: normalizeNullableDoctalkReservationText(rawValue.lastSuccessAt ?? rawValue.last_success_at),
    lastErrorMessage: normalizeDoctalkReservationText(rawValue.lastErrorMessage ?? rawValue.last_error_message),
    naverMemoFieldOrder,
  }
}

export interface CreateHospitalSettingRequest {
  hospitalSettingName: string
  hospitalSettingData: Record<string, any>
}

export interface UpdateHospitalSettingRequest {
  id: number
  hospitalSettingName?: string
  hospitalSettingData?: Record<string, any>
}
